/* Nuitae storefront behaviour. No dependencies. */
(() => {
  const d = document;
  const root = d.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CART_SECTION = 'nuitae-cart-drawer';
  const config = () => window.nuitae || { routes: { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_url: '/cart' } };

  /* Dialogs: menu, search and cart. Native modal dialogs keep the page inert; Tab is kept inside. */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function openDialog(id, trigger) {
    const dialog = d.getElementById(id);
    if (!dialog || dialog.open) return;
    dialog._trigger = trigger || d.activeElement;
    dialog.showModal();
    root.classList.add('n-lock');
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    const auto = dialog.querySelector('[autofocus]');
    if (auto) auto.focus();
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open || dialog._closing) return;
    dialog._closing = true;
    dialog.classList.remove('is-open');
    const finish = () => {
      dialog._closing = false;
      dialog.close();
    };
    if (reducedMotion.matches) finish();
    else setTimeout(finish, 380);
  }

  d.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('n-dialog')) return;
    if (!d.querySelector('dialog.n-dialog[open]')) root.classList.remove('n-lock');
    const trigger = dialog._trigger;
    d.querySelectorAll(`[aria-controls="${dialog.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
    if (trigger && d.contains(trigger) && typeof trigger.focus === 'function') trigger.focus();
  }, true);

  d.addEventListener('cancel', (event) => {
    const dialog = event.target;
    if (dialog instanceof HTMLDialogElement && dialog.classList.contains('n-dialog')) {
      event.preventDefault();
      closeDialog(dialog);
    }
  }, true);

  d.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-n-open]');
    if (opener) {
      event.preventDefault();
      d.querySelectorAll('dialog.n-dialog[open]').forEach((open) => { if (open.id !== opener.dataset.nOpen) closeDialog(open); });
      openDialog(opener.dataset.nOpen, opener);
      return;
    }
    const closer = event.target.closest('[data-n-close]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('n-dialog')) {
      closeDialog(event.target);
    }
  });

  d.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const dialog = d.querySelector('dialog.n-dialog[open]');
    if (!dialog) return;
    // Native modal focus navigation includes wallet buttons in closed shadow roots.
    // A light-DOM-only trap would skip them, especially in a one-time bag.
    if (dialog.querySelector('shopify-accelerated-checkout-cart')) return;
    const items = [...dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === d.activeElement);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (d.activeElement === first || !dialog.contains(d.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (d.activeElement === last || !dialog.contains(d.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  });

  /* Header shadow once the page scrolls. */
  function initHeader() {
    const header = d.querySelector('[data-n-header]');
    if (!header) return;
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 4);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }, { passive: true });
    // A fresh page is at the top. The browser's scroll event also handles
    // restored positions, without forcing layout during initialization.
  }

  /* Sections below the fold fade up 16px over 700ms, children staggered by 80ms.
     Anything already on screen at load is never hidden, so it paints straight away. */
  let revealObserver;
  function initReveal(scope = d) {
    const groups = [...scope.querySelectorAll('[data-n-reveal]:not(.is-revealed):not(.is-waiting)')];
    if (!('IntersectionObserver' in window) || reducedMotion.matches || root.classList.contains('shopify-design-mode')) return;
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const group = entry.target;
          // The observer supplies layout measurements without a synchronous reflow.
          // Leave everything initially in the viewport visible from its first paint.
          if (!group.classList.contains('is-waiting')) {
            if (entry.boundingClientRect.top < entry.rootBounds.bottom + entry.rootBounds.width * 0.06) {
              revealObserver.unobserve(group);
              return;
            }
            group.querySelectorAll('.n-reveal').forEach((el, index) => el.style.setProperty('--n-i', Math.min(index, 8)));
            group.classList.add('is-waiting');
          }
          if (entry.isIntersecting) {
            group.classList.add('is-revealed');
            group.classList.remove('is-waiting');
            revealObserver.unobserve(group);
          }
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
    }
    groups.forEach((group) => revealObserver.observe(group));
  }

  /* Cart */
  function updateCartFromSections(sections) {
    if (!sections || !sections[CART_SECTION]) return;
    const html = new DOMParser().parseFromString(sections[CART_SECTION], 'text/html');
    const incoming = html.querySelector('[data-n-cart-content]');
    const current = d.querySelector('[data-n-cart-content]');
    if (incoming && current) current.replaceWith(incoming);
    const count = incoming ? incoming.dataset.count : null;
    if (count !== null && count !== undefined) {
      d.querySelectorAll('[data-n-cart-count]').forEach((badge) => {
        badge.textContent = count;
        badge.hidden = Number(count) === 0;
      });
      d.querySelectorAll('[data-n-cart-count-text]').forEach((el) => { el.textContent = count; });
      d.querySelectorAll('[data-n-cart-label]').forEach((el) => {
        el.setAttribute('aria-label', `Bag, ${count} ${Number(count) === 1 ? 'item' : 'items'}`);
      });
    }
  }

  async function cartRequest(url, body) {
    const isForm = body instanceof FormData;
    if (isForm) {
      body.append('sections', CART_SECTION);
      body.append('sections_url', window.location.pathname);
    } else {
      body.sections = CART_SECTION;
      body.sections_url = window.location.pathname;
    }
    const response = await fetch(`${url}.js`, {
      method: 'POST',
      headers: isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: isForm ? body : JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      const message = data.description || data.message || 'Something went wrong. Please try again.';
      throw new Error(typeof message === 'string' ? message : 'Something went wrong. Please try again.');
    }
    return data;
  }

  /* Every add to the bag is announced as nuitae:cart:added, one event per line item, for
     analytics and email tools (assets/nuitae-klaviyo.js forwards it to Klaviyo). */
  function announceAdded(items, source) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;
    (items || []).forEach((item) => {
      if (!item || !item.variant_id) return;
      const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      const image = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
      d.dispatchEvent(new CustomEvent('nuitae:cart:added', {
        detail: {
          source,
          product: { id: item.product_id, title: item.product_title, handle: item.handle, type: item.product_type, vendor: item.vendor },
          variant: { id: item.variant_id, title: item.variant_title, sku: item.sku },
          plan: plan ? { id: plan.id, name: plan.name } : null,
          price: item.final_price / 100,
          currency,
          quantity: item.quantity,
          image: image ? new URL(image, window.location.origin).href : null,
          url: new URL(item.url || `/products/${item.handle}`, window.location.origin).href,
        },
      }));
    });
  }

  /* A Night Suite plan is a set of real products (nuitae-plan-resolve): each line carries the
     set's key, so the bag shows and removes them together, and "Set" names it at checkout. */
  function setItems(spec, name, image) {
    const key = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    return spec.split(';').filter(Boolean).map((part, index) => {
      const [id, quantity, plan] = part.split(':');
      const item = { id: Number(id), quantity: Number(quantity), properties: { Set: name, _set: key, _per: quantity } };
      if (index === 0) item.properties._lead = '1';
      if (image) item.properties._image = image;
      if (plan) item.selling_plan = Number(plan);
      return item;
    });
  }

  function selectedUpsellItems(form) {
    const scope = form.closest('[data-n-product-scope]') || d;
    return [...scope.querySelectorAll('[data-n-upsell-input]:checked:not(:disabled)')]
      .map((input) => `${input.value}:1`)
      .join(';');
  }

  /* Shopify's native product wallets read these documented multi-product fields.
     Keep every component's quantity, selling plan and set properties with the form. */
  function syncProductCheckout(form) {
    const fields = form.querySelector('[data-n-checkout-items]');
    if (!fields) return;
    const primary = form.dataset.items || `${form.elements.id.value}:1:${form.elements.selling_plan.value}`;
    const spec = [primary, selectedUpsellItems(form)].filter(Boolean).join(';');
    const signature = `${spec}|${form.dataset.setName}|${form.dataset.setImage}`;
    if (form._checkoutSignature === signature) return;
    form._checkoutSignature = signature;
    const items = spec ? setItems(spec, form.dataset.setName, form.dataset.setImage) : [];
    const fragment = d.createDocumentFragment();
    const field = (name, value) => {
      const input = d.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      fragment.append(input);
    };
    items.forEach((item, index) => {
      const prefix = index ? `shopify-additional-items[${index}]` : '';
      if (index) {
        field(`${prefix}[id]`, item.id);
        field(`${prefix}[quantity]`, item.quantity);
        if (item.selling_plan) field(`${prefix}[selling_plan]`, item.selling_plan);
      } else {
        form.elements.id.value = item.id;
        form.elements.quantity.value = item.quantity;
        form.elements.selling_plan.value = item.selling_plan || '';
      }
      if (index) {
        // Additional-item properties use Shopify's JSON array of key/value pairs,
        // rather than the main item's bracketed property fields.
        field(`${prefix}[properties]`, JSON.stringify(Object.entries(item.properties).map(([key, value]) => ({ key, value: String(value) }))));
      } else {
        Object.entries(item.properties).forEach(([name, value]) => { field(`properties[${name}]`, value); });
      }
    });
    if (!items.length) form.elements.quantity.value = 1;
    fields.replaceChildren(fragment);
    // Reveal the enhanced form only once the selected bundle is synchronized.
    // The noscript forms post complete bundles without this enhancement.
    form.hidden = false;
  }

  function showCart(trigger) {
    if (d.body.classList.contains('n-template-cart')) {
      window.location.href = config().routes.cart_url;
      return;
    }
    openDialog('n-cart', trigger);
  }

  d.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-n-product-form]');
    if (!form) return;
    const variant = form.querySelector('[name="id"]');
    if (!variant || !variant.value) return;
    event.preventDefault();
    if (form._submitting) return;
    form._submitting = true;
    const submitter = event.submitter || form.querySelector('[type="submit"]');
    const express = Boolean(submitter?.hasAttribute('data-n-express-options') || form._expressNext);
    form._expressNext = false;
    const error = form.querySelector('[data-n-form-error]');
    if (error) error.hidden = true;
    const buttons = [...d.querySelectorAll(`[form="${form.id}"][type="submit"]`), ...form.querySelectorAll('[type="submit"]')];
    const walletButtons = [...(form.closest('[data-n-product-scope]')?.querySelectorAll('[data-n-wallet-shortcut]') || [])];
    walletButtons.forEach((button) => { button.disabled = true; });
    buttons.forEach((btn) => btn.classList.add('is-loading'));
    try {
      let data;
      if (form.dataset.items) {
        const extras = selectedUpsellItems(form);
        const spec = [form.dataset.items, extras].filter(Boolean).join(';');
        data = await cartRequest(config().routes.cart_add_url, { items: setItems(spec, form.dataset.setName, form.dataset.setImage) });
      } else {
        const body = new FormData(form);
        if (!body.get('selling_plan')) body.delete('selling_plan');
        data = await cartRequest(config().routes.cart_add_url, body);
      }
      updateCartFromSections(data.sections);
      showCart(submitter);
      if (express) {
        const wallets = d.querySelector('#n-cart .n-cart__accelerated');
        if (wallets) {
          wallets.scrollIntoView({ block: 'center' });
          wallets.focus({ preventScroll: true });
        }
      }
      announceAdded(data.items || [data], 'product');
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      }
    } finally {
      form._submitting = false;
      walletButtons.forEach((button) => { button.disabled = false; });
      buttons.forEach((btn) => btn.classList.remove('is-loading'));
    }
  });

  /* Product-page wallets prepare the selected set before opening the bag's wallets.
     Existing bag items must never cause the current product selection to be skipped. */
  const prepareProductWallets = (event) => {
    const shortcut = event.target instanceof Element ? event.target.closest('[data-n-wallet-shortcut]') : null;
    if (!shortcut || shortcut.disabled) return;
    const wallets = shortcut.closest('[data-n-product-payments]');
    if (!wallets) return;
    const form = wallets.closest('[data-n-product-scope]')?.querySelector('form[data-n-product-form]');
    const buy = form?.querySelector('[data-n-buy]');
    if (!form || !buy || buy.disabled || form._submitting) return;
    event.preventDefault();
    event.stopPropagation();
    form._expressNext = true;
    form.requestSubmit(buy);
  };
  d.addEventListener('click', prepareProductWallets, true);
  // Native buttons provide Enter/Space activation without a second submission handler.
  function initWalletShortcuts(scope = d) {
    const appleButtons = scope.querySelectorAll('[data-n-wallet-shortcut="apple_pay"]');
    if (!appleButtons.length) return;
    let available = false;
    try { available = Boolean(window.ApplePaySession?.canMakePayments()); } catch { /* Native checkout confirms eligibility. */ }
    appleButtons.forEach((button) => { button.hidden = !available; });
  }
  initWalletShortcuts();
  d.addEventListener('shopify:section:load', (event) => initWalletShortcuts(event.target));

  d.addEventListener('click', async (event) => {
    const setChange = event.target.closest('[data-n-set-change]');
    if (setChange) {
      event.preventDefault();
      const content = setChange.closest('[data-n-cart-content]');
      if (content) content.setAttribute('aria-busy', 'true');
      setChange.disabled = true;
      try {
        const data = await cartRequest(config().routes.cart_update_url || '/cart/update', { updates: JSON.parse(setChange.dataset.updates) });
        if (d.body.classList.contains('n-template-cart')) {
          window.location.reload();
          return;
        }
        updateCartFromSections(data.sections);
        const next = d.querySelector('[data-n-cart-content] [data-n-cart-focus]') || d.querySelector('#n-cart .n-dialog__head button');
        if (next) next.focus();
      } catch (err) {
        const message = d.querySelector('[data-n-cart-error]');
        if (message) {
          message.textContent = err.message;
          message.hidden = false;
        }
        setChange.disabled = false;
        if (content) content.removeAttribute('aria-busy');
      }
      return;
    }
    const change = event.target.closest('[data-n-line-change]');
    const add = event.target.closest('[data-n-cart-add]');
    if (!change && !add) return;
    event.preventDefault();
    const el = change || add;
    const content = el.closest('[data-n-cart-content]');
    if (content) content.setAttribute('aria-busy', 'true');
    el.disabled = true;
    try {
      let data;
      if (change) {
        data = await cartRequest(config().routes.cart_change_url, {
          line: Number(change.dataset.line),
          quantity: Number(change.dataset.quantity),
        });
      } else {
        data = await cartRequest(config().routes.cart_add_url, { items: [{ id: Number(add.dataset.variantId), quantity: 1 }] });
        announceAdded(data.items, 'cart');
      }
      if (d.body.classList.contains('n-template-cart')) {
        window.location.reload();
        return;
      }
      updateCartFromSections(data.sections);
      const next = d.querySelector('[data-n-cart-content] [data-n-cart-focus]') || d.querySelector('#n-cart .n-dialog__head button');
      if (next) next.focus();
    } catch (err) {
      const message = d.querySelector('[data-n-cart-error]');
      if (message) {
        message.textContent = err.message;
        message.hidden = false;
      }
      el.disabled = false;
      if (content) content.removeAttribute('aria-busy');
    }
  });

  /* Plan picker: radio group built from selling_plan_allocations. */
  function applyUpsells(scope, planInput) {
    const includedVariants = new Set((planInput.dataset.items || '').split(';').map((part) => part.split(':')[0]).filter(Boolean));
    scope.querySelectorAll('[data-n-upsell]').forEach((card) => {
      const checkbox = card.querySelector('[data-n-upsell-input]');
      const isIncluded = includedVariants.has(card.dataset.variantId);
      if (!checkbox) return;
      if (isIncluded) {
        // An automatically included gift is not an opt-in to buying it on another
        // plan. Save only a genuine optional choice, and preserve it on re-sync.
        if (!checkbox.disabled) checkbox.dataset.beforeIncluded = checkbox.checked ? 'true' : 'false';
        else if (checkbox.dataset.beforeIncluded === undefined) checkbox.dataset.beforeIncluded = 'false';
        checkbox.checked = true;
        checkbox.disabled = true;
      } else {
        if (checkbox.disabled) checkbox.checked = checkbox.dataset.beforeIncluded === 'true';
        checkbox.disabled = false;
      }
      card.classList.toggle('is-included', isIncluded);
      const price = card.querySelector('[data-n-upsell-price]');
      const included = card.querySelector('[data-n-upsell-included]');
      if (price) price.hidden = isIncluded;
      if (included) included.hidden = !isIncluded;
    });
    syncUpsellBundleOffer(scope);
  }

  function syncUpsellBundleOffer(scope) {
    const cards = [...scope.querySelectorAll('[data-n-upsell][data-discounted="true"]')];
    const bundleSelected = cards.length > 0 && cards.every((card) => {
      const input = card.querySelector('[data-n-upsell-input]');
      return input && input.checked && !input.disabled;
    });
    cards.forEach((card) => {
      const regular = card.querySelector('[data-n-upsell-regular]');
      const offer = card.querySelector('[data-n-upsell-offer]');
      const compare = card.querySelector('[data-n-upsell-compare]');
      if (regular) regular.hidden = bundleSelected;
      if (offer) offer.hidden = !bundleSelected;
      if (compare) compare.hidden = !bundleSelected;
      card.classList.toggle('is-bundle-discounted', bundleSelected);
    });
  }

  function applyPlan(picker, input) {
    const form = d.getElementById(picker.dataset.form);
    const scope = picker.closest('[data-n-product-scope]') || d;
    if (form) {
      const id = form.querySelector('input[name="id"]');
      const plan = form.querySelector('input[name="selling_plan"]');
      if (id && input.dataset.variant) id.value = input.dataset.variant;
      if (plan) plan.value = input.dataset.plan || '';
      form.dataset.items = input.dataset.items || '';
      form.dataset.setName = input.dataset.setName || '';
      form.dataset.setImage = input.dataset.image || '';
    }
    scope.querySelectorAll('[data-n-price]').forEach((el) => { el.textContent = input.dataset.price; });
    scope.querySelectorAll('[data-n-compare]').forEach((el) => {
      el.textContent = input.dataset.compare || '';
      el.hidden = !input.dataset.compare;
    });
    scope.querySelectorAll('[data-n-button-label]').forEach((el) => { el.textContent = input.dataset.button; });
    scope.querySelectorAll('[data-n-renewal]').forEach((el) => { el.textContent = input.dataset.renewal; });
    scope.querySelectorAll('[data-n-plan-name]').forEach((el) => { el.textContent = input.dataset.label; });
    scope.querySelectorAll('[data-n-gallery]').forEach((gallery) => {
      gallery.dataset.nSelectedImage = input.dataset.image || '';
      if (gallery._selectImage) gallery._selectImage(input.dataset.image);
    });
    applyUpsells(scope, input);
    const buyable = input.dataset.available !== 'false';
    const buttons = [...scope.querySelectorAll('[data-n-buy]')];
    if (form) buttons.push(...d.querySelectorAll(`[form="${form.id}"][data-n-buy]`));
    buttons.forEach((btn) => { btn.disabled = !buyable; });
    scope.querySelectorAll('[data-n-product-payments]').forEach((el) => { el.hidden = !buyable; });
    if (form) syncProductCheckout(form);
  }

  function initPickers(scope = d) {
    scope.querySelectorAll('[data-n-picker]').forEach((picker) => {
      if (picker._ready) return;
      picker._ready = true;
      picker.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) applyPlan(picker, event.target);
      });
      const checked = picker.querySelector('input[type="radio"]:checked');
      if (checked) applyPlan(picker, checked);
    });
  }

  /* History navigation restores radio inputs after initialization. Keep prices,
     checkout contents and the bag aligned with the restored selection. */
  window.addEventListener('pageshow', (event) => {
    d.querySelectorAll('[data-n-picker]').forEach((picker) => {
      const checked = picker.querySelector('input[type="radio"]:checked');
      if (checked) applyPlan(picker, checked);
    });
    const navigation = performance.getEntriesByType('navigation')[0];
    if (!event.persisted && navigation?.type !== 'back_forward') return;
    fetch(`${config().routes.cart_url}?sections=${CART_SECTION}`, { headers: { Accept: 'application/json' } })
      .then((response) => response.ok ? response.json() : null)
      .then(updateCartFromSections)
      .catch(() => { /* The next cart action also refreshes the bag. */ });
  });

  d.addEventListener('change', (event) => {
    if (!event.target.matches('[data-n-upsell-input]')) return;
    syncUpsellBundleOffer(event.target.closest('[data-n-product-scope]') || d);
    const form = event.target.closest('[data-n-product-scope]')?.querySelector('form[data-n-product-form]');
    if (form) syncProductCheckout(form);
  });

  /* Add-on variant select: the price and the sticky bar follow the chosen variant. */
  d.addEventListener('change', (event) => {
    const select = event.target.closest('[data-n-product-scope] select[name="id"]');
    if (!select) return;
    const option = select.selectedOptions[0];
    const scope = select.closest('[data-n-product-scope]');
    if (!option || !scope) return;
    scope.querySelectorAll('[data-n-variant-price]').forEach((el) => { el.textContent = option.dataset.price; });
    scope.querySelectorAll('[data-n-variant-label]').forEach((el) => { el.textContent = option.dataset.label; });
  });

  /* Sticky buy bar once the main button scrolls away. */
  const stickyBars = new Set();
  let stickyTicking = false;
  function updateSticky() {
    stickyTicking = false;
    stickyBars.forEach((bar) => {
      const target = d.getElementById(bar.dataset.target);
      if (!target || !d.contains(bar)) {
        stickyBars.delete(bar);
        return;
      }
      const next = bar.hasAttribute('data-n-sticky-after') ? bar.closest('.shopify-section')?.nextElementSibling : null;
      // With data-n-sticky-after the bar waits for the next section to come into view.
      const outside = next
        ? next.getBoundingClientRect().top < window.innerHeight * 0.8
        : target.getBoundingClientRect().bottom < 0;
      if (outside === bar.classList.contains('is-visible')) return;
      bar.classList.toggle('is-visible', outside);
      bar.setAttribute('aria-hidden', outside ? 'false' : 'true');
      bar.inert = !outside;
    });
  }
  function initSticky(scope = d) {
    scope.querySelectorAll('[data-n-sticky]').forEach((bar) => {
      bar.inert = true;
      stickyBars.add(bar);
    });
    updateSticky();
  }
  window.addEventListener('scroll', () => {
    if (!stickyTicking && stickyBars.size) {
      stickyTicking = true;
      requestAnimationFrame(updateSticky);
    }
  }, { passive: true });
  window.addEventListener('resize', updateSticky, { passive: true });

  /* Gallery: thumbnails on desktop, swipe with dots on mobile. */
  function initGalleries(scope = d) {
    scope.querySelectorAll('[data-n-gallery]').forEach((gallery) => {
      if (gallery._ready) return;
      gallery._ready = true;
      const track = gallery.querySelector('[data-n-gallery-track]');
      const slides = [...gallery.querySelectorAll('[data-n-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-n-thumb]')];
      const dots = [...gallery.querySelectorAll('[data-n-dot]')];
      const setActive = (index) => {
        thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        slides.forEach((slide, i) => { slide.inert = slides.length > 1 && i !== index; });
      };
      gallery._selectImage = (image, smooth = true) => {
        if (!image) return;
        const index = slides.findIndex((slide) => slide.dataset.nImage === image);
        if (index < 0) return;
        track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto' });
        setActive(index);
      };
      thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => {
          track.scrollTo({ left: slides[index].offsetLeft - track.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
          setActive(index);
        });
      });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActive(slides.indexOf(entry.target));
          });
        }, { root: track, threshold: 0.6 });
        slides.forEach((slide) => observer.observe(slide));
      }
      setActive(0);
      if (gallery.dataset.nSelectedImage) gallery._selectImage(gallery.dataset.nSelectedImage, false);
      initLightbox(gallery);
    });
  }

  /* Lightbox: opens on the image that was clicked, swipes on touch, arrow keys and Esc on a keyboard.
     It is a modal dialog, so focus stays inside and returns to the image on close. */
  function initLightbox(gallery) {
    const box = gallery.querySelector('[data-n-lightbox]');
    if (!box) return;
    const track = box.querySelector('[data-n-lightbox-track]');
    const slides = [...box.querySelectorAll('[data-n-lightbox-slide]')];
    const count = box.querySelector('[data-n-lightbox-count]');
    let index = 0;
    const update = () => {
      if (count) count.textContent = `${index + 1} / ${slides.length}`;
      slides.forEach((slide, i) => { slide.inert = i !== index; });
    };
    const go = (next, smooth = true) => {
      index = (next + slides.length) % slides.length;
      track.scrollTo({ left: slides[index].offsetLeft, behavior: smooth && !reducedMotion.matches ? 'smooth' : 'auto' });
      update();
    };
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && box.open) {
            index = slides.indexOf(entry.target);
            update();
          }
        });
      }, { root: track, threshold: 0.6 });
      slides.forEach((slide) => observer.observe(slide));
    }
    gallery.querySelectorAll('[data-n-zoom]').forEach((button) => {
      button.addEventListener('click', () => {
        openDialog(box.id, button);
        requestAnimationFrame(() => go(Number(button.dataset.nZoom) || 0, false));
      });
    });
    const prev = box.querySelector('[data-n-lightbox-prev]');
    const next = box.querySelector('[data-n-lightbox-next]');
    if (prev) prev.addEventListener('click', () => go(index - 1));
    if (next) next.addEventListener('click', () => go(index + 1));
    box.addEventListener('keydown', (event) => {
      if (slides.length < 2) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(index - 1);
      }
    });
  }

  /* The Wind-Down: 4 seconds in, 6 seconds out. Audio sets the session length when present. */
  const IN = 4000;
  const OUT = 6000;
  const CYCLE = IN + OUT;
  const ease = (t) => 0.5 - Math.cos(Math.PI * t) / 2;

  function initBreath(scope = d) {
    scope.querySelectorAll('[data-n-breath]').forEach((el) => {
      if (el._breath) return;
      const circle = el.querySelector('[data-n-breath-circle]');
      const word = el.querySelector('[data-n-breath-word]');
      const time = el.querySelector('[data-n-breath-time]');
      const controlState = el.querySelector('[data-n-breath-state]');
      const toggle = el.querySelector('[data-n-breath-toggle]');
      const status = el.querySelector('[data-n-breath-status]');
      let total = Number(el.dataset.duration || 300) * 1000;
      const words = { ready: el.dataset.wordReady || 'Tap the circle to begin', in: el.dataset.wordIn || 'Breathe in', out: el.dataset.wordOut || 'Breathe out', done: el.dataset.wordDone || 'Rest now', paused: 'Paused. Tap the circle to carry on.' };
      const audio = el.dataset.audio ? new Audio() : null;
      let audioFailed = false;
      if (audio) {
        audio.preload = 'metadata';
      }

      const state = { playing: false, elapsed: 0, started: 0, frame: 0, phase: '', playToken: 0 };
      el._breath = state;

      const format = (ms) => {
        const s = Math.max(0, Math.ceil((total - ms) / 1000 - 0.001));
        return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
      };

      const actionLabel = () => {
        const remaining = format(state.playing ? clock() : state.elapsed);
        let action = 'Play';
        if (state.playing) action = 'Pause';
        else if (state.elapsed > 0 && state.elapsed < total) action = 'Resume';
        toggle.setAttribute('aria-label', `${action} the Wind-Down, ${remaining} remaining`);
      };

      const render = (ms) => {
        const t = ms % CYCLE;
        let phase;
        let scale;
        if (t < IN) {
          phase = 'in';
          scale = 0.55 + 0.45 * ease(t / IN);
        } else {
          phase = 'out';
          scale = 1 - 0.45 * ease((t - IN) / OUT);
        }
        if (reducedMotion.matches) {
          circle.style.transform = 'scale(0.8)';
          circle.style.opacity = String(0.45 + 0.55 * ((scale - 0.55) / 0.45));
        } else {
          circle.style.transform = `scale(${scale.toFixed(4)})`;
          circle.style.opacity = '';
        }
        if (phase !== state.phase) {
          state.phase = phase;
          word.textContent = words[phase];
        }
        time.textContent = format(ms);
        actionLabel();
      };

      function clock() {
        if (audio && !audioFailed && !audio.paused && Number.isFinite(audio.currentTime)) {
          return audio.currentTime * 1000;
        }
        return state.elapsed + (state.playing ? performance.now() - state.started : 0);
      }

      const tick = () => {
        const ms = clock();
        if (ms >= total) {
          if (audio && !audioFailed) {
            render(total);
            state.frame = requestAnimationFrame(tick);
          } else {
            finish(false);
          }
          return;
        }
        render(ms);
        state.frame = requestAnimationFrame(tick);
      };

      const setUi = (playing) => {
        el.classList.toggle('is-playing', playing);
        el.classList.toggle('is-paused', !playing && state.elapsed > 0 && state.elapsed < total);
        toggle.setAttribute('aria-pressed', String(playing));
        if (controlState) {
          controlState.textContent = playing ? 'Pause' : (state.elapsed > 0 && state.elapsed < total ? 'Resume' : 'Play');
        }
        actionLabel();
      };

      const play = () => {
        if (state.elapsed >= total) {
          state.elapsed = 0;
          state.phase = '';
        }
        state.playing = true;
        state.started = performance.now();
        const playToken = ++state.playToken;
        if (audio && !audioFailed) {
          try { audio.currentTime = state.elapsed / 1000; } catch (e) { /* metadata not loaded yet */ }
          const promise = audio.play();
          if (promise) {
            promise.then(() => {
              if (!state.playing || playToken !== state.playToken) audio.pause();
            }).catch(() => {
              if (playToken !== state.playToken) return;
              audioFailed = true;
              state.started = performance.now();
              status.textContent = 'The audio could not load. The breathing timer is still playing.';
            });
          }
        }
        setUi(true);
        status.textContent = 'Playing. Follow the circle.';
        state.frame = requestAnimationFrame(tick);
      };

      const pause = () => {
        state.elapsed = Math.min(clock(), total);
        state.playing = false;
        state.playToken += 1;
        cancelAnimationFrame(state.frame);
        if (audio) audio.pause();
        setUi(false);
        state.phase = '';
        word.textContent = words.paused;
        status.textContent = 'Paused.';
      };

      const finish = (reset) => {
        cancelAnimationFrame(state.frame);
        state.playing = false;
        state.playToken += 1;
        if (reset && audio && Number.isFinite(audio.duration) && audio.duration > 0) {
          total = audio.duration * 1000;
        }
        state.elapsed = reset ? 0 : total;
        if (audio) {
          audio.pause();
          if (reset) {
            try { audio.currentTime = 0; } catch (e) { /* metadata may have gone away */ }
          }
        }
        circle.style.transform = reducedMotion.matches ? 'scale(0.8)' : 'scale(0.55)';
        word.textContent = words.done;
        time.textContent = reset ? format(0) : '0:00';
        state.phase = '';
        setUi(false);
        status.textContent = 'Done. Close the day softly.';
      };

      if (audio) {
        const syncDuration = () => {
          if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
          total = audio.duration * 1000;
          state.elapsed = Math.min(state.elapsed, total);
          if (!state.playing) {
          if (state.elapsed > 0) render(state.elapsed);
          else {
            time.textContent = format(0);
            actionLabel();
          }
        }
        };
        audio.addEventListener('loadedmetadata', syncDuration);
        audio.addEventListener('durationchange', syncDuration);
        audio.addEventListener('ended', () => finish(true));
        audio.addEventListener('error', () => {
          if (audioFailed) return;
          const elapsed = state.playing && Number.isFinite(audio.currentTime) ? audio.currentTime * 1000 : state.elapsed;
          audioFailed = true;
          state.elapsed = elapsed;
          state.started = performance.now();
          if (state.playing) status.textContent = 'The audio could not load. The breathing timer is still playing.';
        });
        audio.src = el.dataset.audio;
      }
      toggle.addEventListener('click', () => (state.playing ? pause() : play()));
      state.destroy = () => {
        cancelAnimationFrame(state.frame);
        state.playToken += 1;
        if (audio) audio.pause();
      };
      render(0);
      word.textContent = words.ready;
      state.phase = '';
    });
  }

  function init(scope = d) {
    initReveal(scope);
    initPickers(scope);
    initSticky(scope);
    initGalleries(scope);
    initBreath(scope);
  }

  d.addEventListener('shopify:section:load', (event) => init(event.target));
  d.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-n-breath]').forEach((el) => el._breath && el._breath.destroy && el._breath.destroy());
  });

  const start = () => {
    initHeader();
    init();
  };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start);
  else start();
})();
